/**
 * 语音 WebSocket 通信 Hook
 * v6.0 新增：支持语音聊天的 WebSocket 连接
 * v7.0 新增：自动重连机制（指数退避 + Jitter）
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { TokenManager } from '@/shared/services/api'

// 重连配置（放在组件外部，避免重复创建）
const RECONNECT_CONFIG = {
  maxAttempts: 5,           // 最大重连次数
  initialDelay: 1000,       // 初始延迟 1s
  maxDelay: 30000,          // 最大延迟 30s
  backoffMultiplier: 2,     // 指数退避倍数
  jitterFactor: 0.3,        // 抖动因子 ±30%
}

/**
 * 计算重连延迟（指数退避 + Jitter）
 * @param attempt 当前重连次数（从 1 开始）
 */
function getReconnectDelay(attempt: number): number {
  const { initialDelay, maxDelay, backoffMultiplier, jitterFactor } = RECONNECT_CONFIG
  
  // 指数退避：delay = initialDelay * (multiplier ^ (attempt - 1))
  const baseDelay = Math.min(
    initialDelay * Math.pow(backoffMultiplier, attempt - 1),
    maxDelay
  )
  
  // 添加随机抖动：±jitterFactor
  const jitter = baseDelay * jitterFactor * (Math.random() * 2 - 1)
  
  return Math.round(baseDelay + jitter)
}

// WebSocket 消息类型
interface VoiceWSMessage {
  type: string
  [key: string]: unknown
}

// 连接配置
interface VoiceWSConfig {
  projectId: string
  sessionId?: string
  deviceId?: string
  voiceMode?: 'push_to_talk' | 'vad' | 'vad_interruptible'  // v8.3: 补充 vad_interruptible
  vadProvider?: 'client' | 'server'  // v8.3: VAD 责任方，默认 client（客户端 VAD）
  apiVersion?: 'v1' | 'v2'  // v8.1: 支持 v1/v2 API 版本切换
  externalToken?: string  // v8.2: 外部传入的 token（设备测试用）
  endpoint?: 'admin' | 'runtime'  // v8.2: 端点类型，默认 'admin'
  tokenGetter?: () => Promise<string | null>  // v8.2: Token 刷新函数（设备测试用）
  identityId?: string  // v10.0: 身份 ID（用于 PersonaAgentNode 人格模拟）
}

// v8.2: 认证错误关闭码定义（RFC 6455 允许 4000-4999 自定义）
const AUTH_ERROR_CODES = {
  TOKEN_EXPIRED: 4001,      // Token 过期，可刷新后重连
  TOKEN_INVALID: 4003,      // Token 无效
  DEVICE_NOT_FOUND: 4004,   // 设备不存在
  DEVICE_BLOCKED: 4005,     // 设备被封禁
  PERMISSION_DENIED: 4006,  // 没有权限
  PROJECT_NOT_FOUND: 4007,  // 项目不存在
  FLOW_NOT_FOUND: 4008,     // Flow 配置不存在
}

// 可重连的认证错误码（Token 过期可以刷新后重连）
const REFRESHABLE_AUTH_CODES = [AUTH_ERROR_CODES.TOKEN_EXPIRED]

// 不可重连的认证错误码
const FATAL_AUTH_CODES = [
  AUTH_ERROR_CODES.TOKEN_INVALID,
  AUTH_ERROR_CODES.DEVICE_NOT_FOUND,
  AUTH_ERROR_CODES.DEVICE_BLOCKED,
  AUTH_ERROR_CODES.PERMISSION_DENIED,
  AUTH_ERROR_CODES.PROJECT_NOT_FOUND,
  AUTH_ERROR_CODES.FLOW_NOT_FOUND,
]

// TTS 元数据（从 tts_start 消息获取）
export interface TTSMetadata {
  format: 'mp3' | 'pcm' | 'opus'
  sampleRate: number
  channels: number
  bitDepth?: number  // 仅 PCM 格式
  messageId?: string  // 关联的消息 ID
}

// 事件回调
interface VoiceWSCallbacks {
  onConnected?: (sessionId: string, voiceConfig: unknown) => void
  onASRInterim?: (text: string) => void
  onASRFinal?: (text: string) => void
  onContent?: (content: string, isStreaming?: boolean) => void
  onAudio?: (audio: string, format: string) => void
  onTTSStart?: (metadata: TTSMetadata) => void
  onTTSEnd?: () => void
  onCancelled?: (reason?: string) => void  // 🆕 后端确认取消（响应 stop）
  onFlowStart?: (projectId: string) => void
  onFlowEnd?: (sessionId: string) => void
  onNodeEnter?: (nodeId: string, nodeName: string, status?: string) => void
  onFlowEvent?: (event: string, data: Record<string, unknown>) => void
  onError?: (message: string) => void
  onClose?: () => void
  onDeviceCommand?: (command: { action: string; [key: string]: unknown }) => void  // v8.1: 设备指令回调（Admin v2）
  onTokenExpired?: () => void  // v8.2: Token 过期回调（可刷新后重连）
  onAuthError?: (message: string, code: number) => void  // v8.2: 认证错误回调（不可重连）
}

interface VoiceWSState {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
}

/**
 * 语音 WebSocket Hook
 */
export function useVoiceWebSocket(config: VoiceWSConfig, callbacks: VoiceWSCallbacks = {}) {
  const {
    projectId,
    sessionId,
    deviceId,
    voiceMode = 'push_to_talk',
    vadProvider = 'client',  // v8.3: 默认客户端 VAD
    apiVersion = 'v1',
    externalToken,  // v8.2: 外部 token
    endpoint = 'admin',  // v8.2: 端点类型
    tokenGetter,  // v8.2: Token 刷新函数
    identityId,  // v10.0: 身份 ID
  } = config

  const [state, setState] = useState<VoiceWSState>({
    isConnected: false,
    isConnecting: false,
    error: null,
  })

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbacksRef = useRef(callbacks)
  
  // 🆕 isConnectedRef: 用于在回调中获取实时连接状态（避免闭包问题）
  const isConnectedRef = useRef(false)
  
  // 🆕 自动重连控制
  const shouldReconnectRef = useRef(false)  // 是否应该自动重连
  const reconnectAttemptsRef = useRef(0)    // 重连尝试次数
  
  // v8.2: 当前使用的 token（支持动态刷新）
  const currentTokenRef = useRef<string | null>(externalToken || null)
  const tokenGetterRef = useRef(tokenGetter)
  
  // 更新 tokenGetterRef
  useEffect(() => {
    tokenGetterRef.current = tokenGetter
  }, [tokenGetter])

  // 更新 callbacks ref
  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  // 构建 WebSocket URL
  const buildWsUrl = useCallback(() => {
    // v8.2: 优先使用当前缓存的 token，然后是外部 token，最后是 TokenManager
    const token = currentTokenRef.current || externalToken || TokenManager.getToken()
    if (!token) {
      throw new Error('未找到认证Token')
    }

    // v8.2: 根据 endpoint 选择参数名
    const tokenParamName = endpoint === 'runtime' ? 'device_token' : 'token'

    const params = new URLSearchParams({
      project_id: projectId,
      [tokenParamName]: token,  // 动态参数名
      voice_mode: voiceMode,
    })

    // v8.3: VAD Provider（仅非 client 时传递，client 是默认值）
    if (vadProvider && vadProvider !== 'client') {
      params.append('vad_provider', vadProvider)
    }

    if (sessionId) {
      params.append('session_id', sessionId)
    }
    if (deviceId) {
      params.append('device_id', deviceId)
    }
    // v10.0: 身份 ID（用于 PersonaAgentNode 人格模拟）
    if (identityId) {
      params.append('identity_id', identityId)
    }

    // v8.2: 根据 endpoint 和 apiVersion 选择路径
    // v11.2: 支持通过环境变量配置 v2 API 路径
    const v2AdminPath = import.meta.env.VITE_ADMIN_V2_WS_PATH || '/api/v2/admin/chat/ws/multimodal'
    const v2RuntimePath = import.meta.env.VITE_RUNTIME_V2_WS_PATH || '/api/v2/runtime/chat/ws/multimodal'
    const v1VoicePath = '/api/v1/chat/ws/voice'
    
    let voicePath: string
    if (endpoint === 'runtime') {
      // Runtime 端点（设备测试）
      voicePath = v2RuntimePath
    } else {
      // Admin 端点（默认）
      voicePath = apiVersion === 'v2' ? v2AdminPath : v1VoicePath
    }

    // 优先使用独立的 WebSocket URL（云端部署需要绕过 API Gateway）
    const wsBaseUrl = import.meta.env.VITE_WEBSOCKET_URL
    if (wsBaseUrl) {
      // 直接使用配置的 WebSocket URL（指向 Internet-facing NLB）
      console.log('[VoiceWS] 使用独立 WebSocket URL:', wsBaseUrl, 'endpoint:', endpoint, 'API版本:', apiVersion)
      return `${wsBaseUrl}${voicePath}?${params.toString()}`
    }

    // 回退到原有逻辑（本地开发）
    const baseUrl = import.meta.env.VITE_AGENT_ADMIN_API_URL || 'http://localhost:8001/api/v1'

    let wsBase: string
    if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
      // 完整 URL，将 http(s) 转换为 ws(s)，移除路径中的 /api/v1 或 /api/v2
      wsBase = baseUrl.replace(/^http/, 'ws').replace(/\/api\/v\d+$/, '')
    } else {
      // 相对路径，使用当前页面的 host
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      wsBase = `${protocol}//${window.location.host}${baseUrl}`.replace(/\/api\/v\d+$/, '')
    }

    return `${wsBase}${voicePath}?${params.toString()}`
  }, [projectId, sessionId, deviceId, voiceMode, vadProvider, apiVersion, externalToken, endpoint, identityId])

  // 连接 WebSocket
  const connect = useCallback(() => {
    console.log('[VoiceWS] 🔌 connect() called', {
      currentReadyState: wsRef.current?.readyState,
      isOpen: wsRef.current?.readyState === WebSocket.OPEN,
    })
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[VoiceWS] Already connected')
      return
    }

    setState(prev => ({ ...prev, isConnecting: true, error: null }))

    try {
      const url = buildWsUrl()
      console.log('[VoiceWS] 🌐 Connecting to:', url.replace(/token=[^&]+/, 'token=***'))

      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('[VoiceWS] Connected')
        isConnectedRef.current = true
        shouldReconnectRef.current = true  // 🆕 连接成功后启用自动重连
        reconnectAttemptsRef.current = 0   // 🆕 重置重连计数器
        setState({ isConnected: true, isConnecting: false, error: null })
      }

      ws.onmessage = (event) => {
        try {
          const msg: VoiceWSMessage = JSON.parse(event.data)
          console.log('[VoiceWS] Received:', msg.type)

          switch (msg.type) {
            case 'connected':
              callbacksRef.current.onConnected?.(
                msg.session_id as string, 
                msg.voice_config
              )
              break
            case 'asr_interim':
              callbacksRef.current.onASRInterim?.(msg.text as string)
              break
            case 'asr_final':
              callbacksRef.current.onASRFinal?.(msg.text as string)
              break
            case 'content':
              callbacksRef.current.onContent?.(msg.content as string, msg.is_streaming as boolean)
              break
            case 'audio':
              callbacksRef.current.onAudio?.(
                msg.audio as string, 
                msg.format as string
              )
              break
            case 'tts_start':
              callbacksRef.current.onTTSStart?.({
                format: (msg.format as 'mp3' | 'pcm' | 'opus') || 'mp3',
                sampleRate: (msg.sample_rate as number) || 22050,
                channels: (msg.channels as number) || 1,
                bitDepth: msg.bit_depth as number | undefined,
                messageId: msg.message_id as string | undefined,
              })
              break
            case 'tts_end':
              callbacksRef.current.onTTSEnd?.()
              break
            case 'cancelled':
              // 🆕 后端确认取消（响应 stop 消息）
              console.log('[VoiceWS] 收到 cancelled 确认, reason:', msg.reason)
              callbacksRef.current.onCancelled?.(msg.reason as string | undefined)
              break
            case 'flow_start':
              callbacksRef.current.onFlowStart?.(msg.project_id as string)
              callbacksRef.current.onFlowEvent?.('flow_start', { project_id: msg.project_id })
              break
            case 'flow_end':
              callbacksRef.current.onFlowEnd?.(msg.session_id as string)
              callbacksRef.current.onFlowEvent?.('flow_end', { session_id: msg.session_id })
              break
            case 'node_enter':
              // 注意：后端 SSE 返回的是 'name' 字段，不是 'node_name'
              // AgentNode 会发射两次：status="running"（开始）和 status="completed"（完成）
              const nodeName = (msg.name as string) || (msg.node_name as string) || ''
              const nodeStatus = msg.status as string | undefined
              callbacksRef.current.onNodeEnter?.(
                msg.node_id as string,
                nodeName,
                nodeStatus
              )
              callbacksRef.current.onFlowEvent?.('node_enter', {
                node_id: msg.node_id,
                node_name: nodeName,
                status: nodeStatus,
                model: msg.model,
              })
              break
            case 'device_command':
              // v8.1: 设备指令回调（Admin v2 Webhook 模式）
              console.log('[VoiceWS] 收到设备指令:', msg.command)
              callbacksRef.current.onDeviceCommand?.(msg.command as { action: string; [key: string]: unknown })
              break
            case 'thinking':
              // v11.3: Agent Loop 推理中
              console.log('[VoiceWS] thinking 事件: step=', msg.step, 'node=', msg.node)
              callbacksRef.current.onFlowEvent?.('thinking', {
                step: msg.step,
                node: msg.node,
              })
              break
            case 'tool_progress':
              // v11.3: 工具执行中
              console.log('[VoiceWS] tool_progress 事件: tool=', msg.tool, 'desc=', msg.description, 'step=', msg.step)
              callbacksRef.current.onFlowEvent?.('tool_progress', {
                tool: msg.tool,
                description: msg.description,
                step: msg.step,
              })
              break
            case 'tool_call':
              // v11.3: 工具调用事件
              console.log('[VoiceWS] tool_call 事件:', msg)
              callbacksRef.current.onFlowEvent?.('tool_call', msg)
              break
            case 'silent_op':
              // v11.3: 静默操作
              console.log('[VoiceWS] silent_op 事件: operation=', msg.operation)
              callbacksRef.current.onFlowEvent?.('silent_op', {
                operation: msg.operation,
              })
              break
            case 'route_result':
              // v11.3: 路由结果
              console.log('[VoiceWS] route_result 事件:', msg)
              callbacksRef.current.onFlowEvent?.('route_result', {
                node: msg.node,
                condition: msg.condition,
                target: msg.target,
                reason: msg.reason,
              })
              break
            case 'voice_id_update':
              // v11.3: 语音 ID 更新
              console.log('[VoiceWS] voice_id_update 事件: voice_id=', msg.voice_id)
              callbacksRef.current.onFlowEvent?.('voice_id_update', {
                voice_id: msg.voice_id,
              })
              break
            case 'voice_config_update':
              // v11.3: 语音配置更新
              console.log('[VoiceWS] voice_config_update 事件:', msg.voice_config)
              callbacksRef.current.onFlowEvent?.('voice_config_update', {
                voice_config: msg.voice_config,
              })
              break
            case 'error':
              callbacksRef.current.onError?.(msg.message as string)
              setState(prev => ({ ...prev, error: msg.message as string }))
              break
            case 'pong':
              // 心跳响应
              break
            default:
              console.log('[VoiceWS] Unknown message type:', msg.type)
          }
        } catch (err) {
          console.error('[VoiceWS] Failed to parse message:', err)
        }
      }

      ws.onerror = (event) => {
        console.error('[VoiceWS] Error:', event)
        setState(prev => ({ 
          ...prev, 
          isConnecting: false, 
          error: 'WebSocket 连接错误' 
        }))
      }

      ws.onclose = (event) => {
        console.log('[VoiceWS] Closed:', event.code, event.reason)
        isConnectedRef.current = false
        setState({ isConnected: false, isConnecting: false, error: null })
        callbacksRef.current.onClose?.()
        
        // v8.2: 根据关闭码区分处理
        // 1000 = 正常关闭, 1001 = 离开页面
        if (event.code === 1000 || event.code === 1001) {
          console.log('[VoiceWS] 正常关闭，不重连')
          return
        }
        
        // 4xxx 认证错误码处理
        if (FATAL_AUTH_CODES.includes(event.code)) {
          // 致命认证错误，不重连
          console.warn(`[VoiceWS] ❌ 认证错误 (${event.code})，停止重连:`, event.reason)
          shouldReconnectRef.current = false
          reconnectAttemptsRef.current = 0
          callbacksRef.current.onAuthError?.(event.reason || `认证失败 (${event.code})`, event.code)
          return
        }
        
        if (REFRESHABLE_AUTH_CODES.includes(event.code)) {
          // Token 过期，尝试刷新后重连
          console.log('[VoiceWS] 🔑 Token 过期，尝试刷新...')
          callbacksRef.current.onTokenExpired?.()
          
          if (tokenGetterRef.current) {
            // 有 tokenGetter，尝试刷新 token 后重连
            tokenGetterRef.current().then((newToken) => {
              if (newToken) {
                console.log('[VoiceWS] ✅ Token 刷新成功，重新连接...')
                currentTokenRef.current = newToken
                reconnectAttemptsRef.current = 0  // 重置重连计数
                connect()
              } else {
                console.warn('[VoiceWS] ❌ Token 刷新失败（返回空），停止重连')
                callbacksRef.current.onAuthError?.('Token 刷新失败', event.code)
              }
            }).catch((err) => {
              console.error('[VoiceWS] ❌ Token 刷新异常:', err)
              callbacksRef.current.onAuthError?.('Token 刷新失败: ' + (err instanceof Error ? err.message : String(err)), event.code)
            })
          } else {
            // 没有 tokenGetter，通知上层处理
            console.warn('[VoiceWS] ⚠️ Token 过期但未配置 tokenGetter，停止重连')
            callbacksRef.current.onAuthError?.('Token 已过期，请重新开始', event.code)
          }
          return
        }
        
        // 其他错误码（网络问题等），正常重连
        if (shouldReconnectRef.current) {
          if (reconnectAttemptsRef.current < RECONNECT_CONFIG.maxAttempts) {
            reconnectAttemptsRef.current++
            const delay = getReconnectDelay(reconnectAttemptsRef.current)
            console.log(`[VoiceWS] 🔄 尝试重连 (${reconnectAttemptsRef.current}/${RECONNECT_CONFIG.maxAttempts})，延迟 ${delay}ms...`)
            reconnectTimeoutRef.current = setTimeout(() => {
              connect()
            }, delay)
          } else {
            console.warn('[VoiceWS] ❌ 达到最大重连次数，停止重连')
            shouldReconnectRef.current = false
            reconnectAttemptsRef.current = 0
          }
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '无法建立连接'
      setState({ isConnected: false, isConnecting: false, error: errorMsg })
      callbacksRef.current.onError?.(errorMsg)
    }
  }, [buildWsUrl])

  // 断开连接
  const disconnect = useCallback(() => {
    // 🆕 主动断开时禁用自动重连
    shouldReconnectRef.current = false
    reconnectAttemptsRef.current = 0
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected')  // 🆕 使用正常关闭码
      wsRef.current = null
    }

    isConnectedRef.current = false
    setState({ isConnected: false, isConnecting: false, error: null })
  }, [])

  // 发送消息
  const sendMessage = useCallback((message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        data: { message },
      }))
      return true
    }
    return false
  }, [])

  // 发送音频数据
  const sendAudio = useCallback((audio: string, format: string = 'webm', sampleRate: number = 16000) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'audio',
        data: { audio, format, sample_rate: sampleRate },
      }))
      return true
    }
    return false
  }, [])

  // 开始录音（VAD 模式）
  const sendAudioStart = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'audio_start' }))
      return true
    }
    return false
  }, [])

  // 结束录音
  const sendAudioEnd = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'audio_end' }))
      return true
    }
    return false
  }, [])

  // 停止执行（支持传入原因，用于打断等场景）
  const sendStop = useCallback((reason?: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ 
        type: 'stop',
        reason: reason || 'User cancelled',
      }))
      return true
    }
    return false
  }, [])

  // 发送心跳
  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }))
      return true
    }
    return false
  }, [])

  // 清理
  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    ...state,
    isConnectedRef,  // 🆕 暴露 ref，供回调中获取实时连接状态
    connect,
    disconnect,
    sendMessage,
    sendAudio,
    sendAudioStart,
    sendAudioEnd,
    sendStop,
    sendPing,
  }
}

